/** The Itinerary tab of a TOUR-type tour (plan §5): the itinerary on the page's one draft
 *  (D10) — no store or save of its own; its changes go into the Changes ledger with the
 *  rest of the tour and save in the same PATCH.
 *
 *  - **Outline** (290 px): the days and their stops; select a row to open it.
 *  - **Inspector**: the selected stop (its fields, then its activity rows) or day. Its grid
 *    follows the width it has beside the docked Changes (Zero-Re-Layout).
 *  - **Toolbar**: the tour-level route file (B6), and what keeps Save off (the rules'
 *    blocking issues, each a link to its row).
 *  - **The phone preview** is the page's dock (Preview | Changes): the workspace publishes
 *    the day its selection is in (`PreviewTarget`), and the phone follows it.
 *
 *  Performance (plan §5 budget): memoised on its props, so the page's per-keystroke
 *  renders stop here; the record editor's context is read once by the bridge; every row and
 *  input subscribes to its own record or path. No rich-text editor is mounted at rest. */
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Alert, Box, Link, Tooltip, Typography } from '@mui/material';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { parseRecordKey } from '@/features/records/draft/changeSet';
import { useDraftStable } from '@/features/records/draft/DraftProvider';
import { OutlineEditorBridge, useOutlineEditor } from '@/features/records/outline/bridge';
import { useBlockingIssues } from '@/features/records/outline/issues';
import { useDraftSelector } from '@/features/records/outline/store';
import { useRecordPreviewTarget } from '@/features/records/preview/RecordPreviewProvider';

import { useTourRecord } from '../record/TourRecordContext';
import { tourColumns } from '../record/views/TourTabView';

import { DayInspector, SlotInspector, TourKmlField } from './Inspector';
import { ACTIVITY, DAY, draftPlacement, SLOT } from './model';
import { Outline, OUTLINE_WIDTH, type Selection } from './Outline';
import { outlineKey, outlineRows, type OutlineRow } from './outlineRows';
import { ExpandBus, ItineraryProvider, useItinerary, type ItineraryContextValue } from './store';

const GAP_PX = 16;
/** DESIGN "Record editor": field columns of at least 232 px. */
export const FIELD_COLUMN_PX = 232;

/** The inspector's field columns at its EDIT-mode width (beside the docked Changes): at
 *  1440 px with the nav open (1152 px of content) the inspector has 450 px — one column. */
export function inspectorColumns(contentWidth: number): number {
  if (contentWidth === 0) return 2;
  const { editWidth } = tourColumns(contentWidth);
  const inspector = editWidth - OUTLINE_WIDTH - GAP_PX;
  return Math.max(1, Math.min(3, Math.floor((inspector + GAP_PX) / (FIELD_COLUMN_PX + GAP_PX))));
}

export interface ItineraryWorkspaceProps {
  editing: boolean;
  canEdit: boolean;
  /** The page content's width (0 = unmeasured). */
  width: number;
}

export const ItineraryWorkspace = memo(function ItineraryWorkspace({ editing, canEdit, width }: ItineraryWorkspaceProps) {
  const { detail, baseline, itineraryIssues } = useTourRecord();
  const [expand] = useState(() => new ExpandBus());
  const ctx = useMemo<ItineraryContextValue>(
    () => ({
      baseline,
      tourRec: baseline.rec,
      editing,
      canEdit,
      lockReason: canEdit ? null : 'Needs the console_tours_manage permission',
      tenantId: detail.tenant?.id ?? 0,
      tourId: detail.id,
      isBespoke: Boolean(detail.is_bespoke),
      startDate: detail.start_date ?? null,
      issues: itineraryIssues,
      expand,
    }),
    [baseline, editing, canEdit, detail.tenant?.id, detail.id, detail.is_bespoke, detail.start_date, itineraryIssues, expand],
  );
  if (!detail.itinerary) {
    return <Alert severity="info">This tour has no itinerary scaffolded yet.</Alert>;
  }
  return (
    <ItineraryProvider value={ctx}>
      <OutlineEditorBridge>
        <WorkspaceBody columns={inspectorColumns(width)} />
      </OutlineEditorBridge>
    </ItineraryProvider>
  );
});

function firstDay(rows: readonly OutlineRow[]): Selection {
  const day = rows.find((r) => r.kind === 'day' && !r.removed);
  return day ? { kind: 'day', id: day.id } : null;
}

const WorkspaceBody = memo(function WorkspaceBody({ columns }: { columns: number }) {
  const { baseline, tourRec, expand } = useItinerary();
  const { itineraryFocus } = useTourRecord();
  const editor = useOutlineEditor();
  const { store } = useDraftStable();
  const rows = useDraftSelector((entries) => outlineRows(entries, baseline.paths, tourRec), outlineKey);
  const [selection, setSelection] = useState<Selection>(() => firstDay(rows));

  // A selected row that is gone (a new row saved under its new id, a discard): the row now
  // at its place, else the first day.
  const lastIndex = useRef(0);
  const index = rows.findIndex((r) => selection !== null && r.kind === selection.kind && r.id === selection.id);
  if (index >= 0) lastIndex.current = index;
  useEffect(() => {
    if (index >= 0 || rows.length === 0) return;
    const at = rows[Math.min(lastIndex.current, rows.length - 1)];
    setSelection(at ? { kind: at.kind, id: at.id } : firstDay(rows));
  }, [index, rows]);

  const selectedDayIndex = useMemo(() => {
    const row = rows[index];
    if (!row) return 0;
    const dayId = row.kind === 'day' ? row.id : row.dayId;
    const day = rows.find((r) => r.kind === 'day' && r.id === dayId);
    return day?.kind === 'day' && day.index !== null ? day.index : 0;
  }, [rows, index]);
  // The phone follows the selection (plan §4).
  const previewTarget = useRecordPreviewTarget();
  useEffect(() => {
    previewTarget?.setDayIndex(selectedDayIndex);
  }, [previewTarget, selectedDayIndex]);

  // To a field of the itinerary that may not be on screen (a ledger row, the block
  // reason's "show"): select its row, open its activity, then focus the field once it has
  // rendered.
  const goTo = useCallback(
    (rec: string, path: string) => {
      const { entity, id } = parseRecordKey(rec);
      if (entity === DAY) setSelection({ kind: 'day', id });
      else if (entity === SLOT) setSelection({ kind: 'slot', id });
      else if (entity === ACTIVITY) {
        const at = draftPlacement(store.getState(), baseline.paths, tourRec).activities.get(id);
        if (!at) return;
        setSelection({ kind: 'slot', id: at.slotId });
        expand.emit(id);
      } else return;
      window.setTimeout(() => requestAnimationFrame(() => editor.focusField(rec, path)), 0);
    },
    [store, baseline, tourRec, expand, editor],
  );
  useEffect(() => itineraryFocus.listen(goTo), [itineraryFocus, goTo]);

  const onCreatedSlot = useCallback((id: number) => setSelection({ kind: 'slot', id }), []);

  return (
    <Box data-itin-workspace sx={{ display: 'grid', gap: 1.5 }}>
      {/* One line at every width: the route file takes the room. */}
      <Box data-itin-toolbar sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'nowrap' }}>
        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
          <TourKmlField />
        </Box>
      </Box>
      <IssuesSummary onShow={goTo} />
      <Box sx={{ display: 'flex', gap: `${GAP_PX}px`, alignItems: 'flex-start' }}>
        <Outline rows={rows} selection={selection} onSelect={setSelection} />
        <Box data-itin-inspector-column sx={{ flex: 1, minWidth: 0 }}>
          {selection === null ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
              Select a day or a stop in the outline.
            </Typography>
          ) : selection.kind === 'slot' ? (
            <SlotInspector key={`s${selection.id}`} slotId={selection.id} columns={columns} />
          ) : (
            <DayInspector key={`d${selection.id}`} dayId={selection.id} columns={columns} onCreated={onCreatedSlot} />
          )}
        </Box>
      </Box>
    </Box>
  );
});

/** What keeps Save off in the itinerary: its own reserved line (24 px) under the toolbar,
 *  in both modes — one line, cut with an ellipsis, the whole text in its tooltip and its
 *  accessible name; "show" goes to the field (its stop, its activity row). */
function IssuesSummary({ onShow }: { onShow: (rec: string, path: string) => void }) {
  const { issues, editing } = useItinerary();
  const blocking = useBlockingIssues(issues);
  const first = blocking[0];
  const text = first ? `Can’t save the itinerary: ${first.message}${blocking.length > 1 ? ` (+${blocking.length - 1} more)` : ''}` : '';
  return (
    <Box data-itin-issues sx={{ height: 24, display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      {editing && first && (
        <>
          <ErrorOutlineIcon aria-hidden sx={{ fontSize: 18, color: 'error.main', flex: 'none' }} />
          <Tooltip title={text}>
            <Typography variant="body2" noWrap aria-label={text} data-itin-block-reason sx={{ minWidth: 0, flex: '0 1 auto' }}>
              {text}
            </Typography>
          </Tooltip>
          <Link component="button" type="button" variant="body2" onClick={() => onShow(first.rec, first.path)} sx={{ flex: 'none' }}>
            show
          </Link>
        </>
      )}
    </Box>
  );
}
